import type {AriaButtonLinkType} from '@aboutyou/ui-components';
import React, {useEffect, useRef} from 'react';
import {useHandleNavigationWithSpecialKeys} from './useHandleNavigationWithSpecialKeys';

interface LinkButtonProps {
  to: string;
  className?: string;
  dataTestId?: string;
  considerScrollTopAnchor?: boolean;
}

/**
 * The LinkButton component simulates a link using a button.
 *
 * This component supports keyboard navigation (Enter or Space key press).
 *
 * It also listens for mouse events:
 * Ctrl + Click or Meta + Click or Middle mouse button click: Opens link in a new tab.
 * Shift + Click: Opens link in a new window.
 * Normal Click or Enter or Space: Navigates to the link in the current tab.
 *
 * Note: The actual behavior of opening links in new tabs or windows is browser-dependent and may
 * also depend on the user's browser settings.
 */
export const LinkButton: React.FC<
  React.PropsWithChildren<LinkButtonProps & AriaButtonLinkType>
> = ({
  to,
  className,
  dataTestId,
  considerScrollTopAnchor,
  children,
  'aria-label': ariaLabel,
}) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const handleClick = useHandleNavigationWithSpecialKeys({
    to,
    considerScrollTopAnchor,
  });

  useEffect(() => {
    const button = ref.current;
    const keyListener = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleClick(event as never);
      }
    };

    button?.addEventListener('keydown', keyListener);
    return () => button?.removeEventListener('keydown', keyListener);
  }, [ref, handleClick]);

  return (
    <button
      data-testid={dataTestId}
      ref={ref}
      className={className}
      onClick={handleClick}
      role="link"
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
};
